<script setup>
import { markRaw } from 'vue';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { MiniMap } from '@vue-flow/minimap';
import StartNode from './nodes/StartNode.vue';
import MessageNode from './nodes/MessageNode.vue';
import WhatsAppListNode from './nodes/WhatsAppListNode.vue';
import ButtonsNode from './nodes/ButtonsNode.vue';
import MediaNode from './nodes/MediaNode.vue';
import HandoffNode from './nodes/HandoffNode.vue';
import EndNode from './nodes/EndNode.vue';
import { START_NODE_ID } from './utils/nodeFactory';

const nodes = defineModel('nodes', { type: Array, default: () => [] });
const edges = defineModel('edges', { type: Array, default: () => [] });

const emit = defineEmits(['nodeSelected', 'dropNode', 'changed']);

const nodeTypes = {
  start: markRaw(StartNode),
  message: markRaw(MessageNode),
  whatsapp_list: markRaw(WhatsAppListNode),
  buttons: markRaw(ButtonsNode),
  media: markRaw(MediaNode),
  handoff: markRaw(HandoffNode),
  end: markRaw(EndNode),
};

const { screenToFlowCoordinate, fitView } = useVueFlow();

defineExpose({ fitView });

// only real modifications mark the flow as dirty (not selection/measure events)
const onNodesChange = changes => {
  const meaningful = changes.some(
    change =>
      change.type === 'remove' ||
      change.type === 'add' ||
      (change.type === 'position' && change.dragging === false)
  );
  if (meaningful) emit('changed');
};

const onEdgesChange = changes => {
  if (changes.some(change => ['remove', 'add'].includes(change.type))) {
    emit('changed');
  }
};

// spec: fallback outputs may only go to message / list / handoff / end
const FALLBACK_TARGETS = ['message', 'whatsapp_list', 'media', 'handoff', 'end'];

const isValidConnection = connection => {
  if (!connection.source || !connection.target) return false;
  if (connection.source === connection.target) return false;
  if (connection.target === START_NODE_ID) return false;

  const targetNode = nodes.value.find(node => node.id === connection.target);
  if (!targetNode) return false;
  if (
    connection.sourceHandle === 'fallback' &&
    !FALLBACK_TARGETS.includes(targetNode.type)
  ) {
    return false;
  }
  return true;
};

const onConnect = connection => {
  if (!isValidConnection(connection)) return;
  // one connection per output: connecting again replaces the previous edge.
  // Single assignment: with defineModel the prop syncs asynchronously, so a
  // filter-assign followed by push would mutate the stale array and lose the
  // new edge.
  edges.value = [
    ...edges.value.filter(
      edge =>
        !(
          edge.source === connection.source &&
          edge.sourceHandle === connection.sourceHandle
        )
    ),
    {
      id: `e-${connection.source}-${connection.sourceHandle}-${connection.target}`,
      source: connection.source,
      sourceHandle: connection.sourceHandle,
      target: connection.target,
      targetHandle: 'in',
    },
  ];
  emit('changed');
};

const onNodeClick = ({ node }) => emit('nodeSelected', node.id);
const onPaneClick = () => emit('nodeSelected', null);

const onDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const onDrop = event => {
  const type = event.dataTransfer?.getData('application/fluxobuilder-node');
  if (!type) return;
  const position = screenToFlowCoordinate({ x: event.clientX, y: event.clientY });
  emit('dropNode', { type, position });
};
</script>

<template>
  <div class="w-full h-full" @drop="onDrop" @dragover="onDragOver">
    <VueFlow
      v-model:nodes="nodes"
      v-model:edges="edges"
      :node-types="nodeTypes"
      :is-valid-connection="isValidConnection"
      :default-viewport="{ x: 80, y: 80, zoom: 0.85 }"
      :min-zoom="0.2"
      :max-zoom="2"
      :delete-key-code="['Backspace', 'Delete']"
      fit-view-on-init
      @connect="onConnect"
      @node-click="onNodeClick"
      @pane-click="onPaneClick"
      @nodes-change="onNodesChange"
      @edges-change="onEdgesChange"
    >
      <Background :gap="18" />
      <MiniMap
        pannable
        zoomable
        node-color="#64748b"
        mask-color="rgba(23, 23, 28, 0.65)"
        class="!bg-n-solid-2 rounded-lg border border-n-weak"
      />
    </VueFlow>
  </div>
</template>
